import { useId, useMemo, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ChatMessageSource } from '../../../types/chatMessage';
import { LiksChatMessageSourceRow } from './LiksChatMessageSourceRow';
import {
  resolveSourceKnowledgeBaseId,
  sourceDisplayName,
} from './chatMessageSourceUtils';
import './LiksChatMessageSources.css';

export type LiksChatMessageDocumentPreviewRenderProps = {
  isOpen: boolean;
  knowledgeBaseId: string;
  documentId: string;
  title: string;
  closeLabel: string;
  loadingLabel: string;
  errorLabel: string;
  inaccessibleLabel?: string;
  unavailableLabel: string;
  onClose: () => void;
};

export type LiksChatMessageSourcesProps = {
  sources: ChatMessageSource[];
  sourcesLabel: string;
  sourcesEmptyLabel: string;
  sourceTypeColumnLabel: string;
  sourceFileColumnLabel: string;
  sourceUrlColumnLabel: string;
  sourceInaccessibleLabel: string;
  getSourceTypeLabel: (sourceType: string | undefined) => string;
  openDocumentLabel: string;
  openTrainingExampleLabel?: string;
  documentPreviewCloseLabel: string;
  documentPreviewLoadingLabel: string;
  documentPreviewErrorLabel: string;
  documentPreviewInaccessibleLabel?: string;
  documentPreviewUnavailableLabel: string;
  sourceTrainingDeletedLabel?: string;
  /**
   * Used when a source has `documentId` but no `knowledgeBaseId`
   * (e.g. chatbot conversations tied to one knowledge database).
   */
  fallbackKnowledgeBaseId?: string;
  /**
   * Knowledge databases accessible to the current user.
   * `undefined` while that list has not loaded yet.
   */
  knowledgeBases?: ReadonlyArray<{ id: string; name: string }>;
  /** Optional note shown below the sources table when expanded. */
  sourcesHint?: string;
  /** Opens a training-correction source (chatbot conversations). */
  onOpenTrainingExample?: (source: ChatMessageSource) => void;
  /**
   * Renders the knowledge-document preview modal (feature-connected).
   * Liks stays presentational and does not import query/feature hooks.
   */
  renderDocumentPreview?: (
    props: LiksChatMessageDocumentPreviewRenderProps,
  ) => ReactNode;
  /** Right-side actions (retry / copy) rendered on the same row as the toggle. */
  trailingActions?: ReactNode;
};

export function LiksChatMessageSources({
  sources,
  sourcesLabel,
  sourcesEmptyLabel,
  sourceTypeColumnLabel,
  sourceFileColumnLabel,
  sourceUrlColumnLabel,
  sourceInaccessibleLabel,
  getSourceTypeLabel,
  openDocumentLabel,
  openTrainingExampleLabel,
  documentPreviewCloseLabel,
  documentPreviewLoadingLabel,
  documentPreviewErrorLabel,
  documentPreviewInaccessibleLabel,
  documentPreviewUnavailableLabel,
  sourceTrainingDeletedLabel,
  fallbackKnowledgeBaseId,
  knowledgeBases,
  sourcesHint,
  onOpenTrainingExample,
  renderDocumentPreview,
  trailingActions,
}: LiksChatMessageSourcesProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewSource, setPreviewSource] = useState<ChatMessageSource | null>(
    null,
  );
  const tableId = useId();
  const accessibleKnowledgeBaseIds = useMemo(() => {
    if (knowledgeBases == null) {
      return null;
    }
    return new Set(knowledgeBases.map((knowledgeBase) => knowledgeBase.id));
  }, [knowledgeBases]);

  const previewDocumentId = previewSource?.documentId?.trim() ?? '';
  const previewKnowledgeBaseId = previewSource
    ? resolveSourceKnowledgeBaseId(
        previewSource,
        fallbackKnowledgeBaseId,
        knowledgeBases,
      )
    : '';

  return (
    <>
      <div className="liks-chat-message__actions-row">
        <button
          type="button"
          className="liks-chat-message__sources-toggle"
          aria-expanded={isExpanded}
          aria-controls={tableId}
          onClick={() => setIsExpanded((current) => !current)}
        >
          <span>{sourcesLabel}</span>
          <ChevronDown
            className={[
              'liks-chat-message__sources-chevron',
              isExpanded && 'liks-chat-message__sources-chevron--open',
            ]
              .filter(Boolean)
              .join(' ')}
            strokeWidth={2}
            aria-hidden
          />
        </button>
        {trailingActions != null && (
          <div className="liks-chat-message__actions-end">
            {trailingActions}
          </div>
        )}
      </div>

      {isExpanded &&
        (sources.length === 0 ? (
          <p id={tableId} className="liks-chat-message__sources-empty">
            {sourcesEmptyLabel}
          </p>
        ) : (
          <div className="liks-chat-message__sources-table-wrap">
            <table
              id={tableId}
              className="liks-chat-message__sources-table"
              aria-label={sourcesLabel}
            >
              <thead>
                <tr>
                  <th scope="col">{sourceTypeColumnLabel}</th>
                  <th scope="col">{sourceFileColumnLabel}</th>
                  <th scope="col">{sourceUrlColumnLabel}</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((source, index) => (
                  <LiksChatMessageSourceRow
                    key={
                      source.id ?? `${source.documentId ?? 'source'}-${index}`
                    }
                    source={source}
                    getSourceTypeLabel={getSourceTypeLabel}
                    openDocumentLabel={openDocumentLabel}
                    openTrainingExampleLabel={openTrainingExampleLabel}
                    sourceInaccessibleLabel={sourceInaccessibleLabel}
                    sourceTrainingDeletedLabel={sourceTrainingDeletedLabel}
                    fallbackKnowledgeBaseId={fallbackKnowledgeBaseId}
                    knowledgeBases={knowledgeBases}
                    accessibleKnowledgeBaseIds={accessibleKnowledgeBaseIds}
                    onOpenDocument={setPreviewSource}
                    onOpenTrainingExample={onOpenTrainingExample}
                  />
                ))}
              </tbody>
            </table>
            {sourcesHint != null && sourcesHint.trim() !== '' && (
              <p className="liks-chat-message__sources-hint">{sourcesHint}</p>
            )}
          </div>
        ))}

      {renderDocumentPreview != null &&
        previewDocumentId.length > 0 &&
        previewKnowledgeBaseId.length > 0 &&
        renderDocumentPreview({
          isOpen: previewSource != null,
          knowledgeBaseId: previewKnowledgeBaseId,
          documentId: previewDocumentId,
          title: sourceDisplayName(previewSource!),
          closeLabel: documentPreviewCloseLabel,
          loadingLabel: documentPreviewLoadingLabel,
          errorLabel: documentPreviewErrorLabel,
          inaccessibleLabel: documentPreviewInaccessibleLabel,
          unavailableLabel: documentPreviewUnavailableLabel,
          onClose: () => setPreviewSource(null),
        })}
    </>
  );
}
